import usePageMeta from '../hooks/usePageMeta';
import React, { useState } from 'react';
import { Sparkles, Calendar, Clock } from 'lucide-react';
import CustomOrderStrip from '../components/CustomOrderStrip';
import { toast } from 'sonner';

const TIME_SLOTS = ['11:00 AM', '12:00 PM', '2:00 PM', '4:00 PM', '6:00 PM'];

export default function DiwaliSlots() {
  usePageMeta('Diwali Custom Slots | Hitera – Reserve Your Festive Outfit', 'Reserve your Diwali custom-order slot with Hitera. Bespoke designer wear tailored to your measurements, crafted in 2–3 weeks and delivered before Diwali.');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) { toast.error('Please add your name and phone number'); return; }
    const text = `Hi Hitera! I'd like to reserve a Diwali custom-order slot. ✨
Name: ${name.trim()}
Phone: ${phone.trim()}
Preferred consultation: ${date || 'Flexible'}${time ? ` at ${time}` : ''}
Notes: ${notes.trim() || '—'}`;
    window.open(`https://wa.me/919653629294?text=${encodeURIComponent(text)}`, '_blank');
    toast.success('Opening WhatsApp…', { description: 'We will confirm your slot personally.' });
  };

  return (
    <div className="bg-cream" data-testid="diwali-page">
      {/* Hero */}
      <section className="relative bg-burgundy-950 text-cream overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #C6A15B 0, transparent 40%), radial-gradient(circle at 80% 70%, #C6A15B 0, transparent 40%)' }} />
        <div className="relative max-w-[900px] mx-auto px-6 py-24 md:py-32 text-center">
          <p className="text-gold tracking-luxe text-xs uppercase mb-5 fade-up flex items-center justify-center gap-2"><Sparkles size={14} /> Festive 2026 <Sparkles size={14} /></p>
          <h1 className="font-display text-5xl md:text-7xl mb-6 fade-up">The Diwali Edit</h1>
          <p className="text-cream/70 max-w-xl mx-auto text-sm md:text-base leading-relaxed fade-up mb-4">
            A custom HITÉRA creation, tailored to your exact measurements and delivered before Diwali — 8 November.
          </p>
          <p className="text-gold text-sm tracking-wide fade-up mb-10">Crafted in 2–3 weeks · Limited atelier slots each festive season</p>
          <a href="#reserve" data-testid="diwali-hero-cta" className="inline-flex items-center gap-3 bg-gold text-burgundy-950 px-10 py-4 text-xs tracking-wideluxe uppercase hover:bg-gold-light transition-colors fade-up">
            Reserve Your Slot
          </a>
        </div>
      </section>

      {/* How it works */}
      <CustomOrderStrip />

      {/* Reserve form */}
      <section id="reserve" className="max-w-[640px] mx-auto px-6 py-16 md:py-20">
        <div className="text-center mb-10">
          <p className="text-gold-dark tracking-luxe text-xs uppercase mb-3">Reserve Your Slot</p>
          <h2 className="font-display text-3xl md:text-4xl text-burgundy-900 mb-3">Book Your Diwali Consultation</h2>
          <p className="text-sm text-burgundy-900/60 leading-relaxed">Tell us a little about you and we'll confirm your slot personally on WhatsApp. Consultations happen at our Kandivali West atelier, Mon–Sat, 11 AM – 8 PM.</p>
        </div>
        <form onSubmit={submit} className="bg-white border border-gold/25 p-6 md:p-8 space-y-5">
          <div>
            <label className="block text-[11px] tracking-wideluxe uppercase text-burgundy-900/60 mb-1.5">Your Name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="diwali-name-input" className="w-full border border-burgundy-900/15 px-4 py-3 text-sm focus:outline-none focus:border-gold" placeholder="e.g. Aishwarya Sharma" />
          </div>
          <div>
            <label className="block text-[11px] tracking-wideluxe uppercase text-burgundy-900/60 mb-1.5">Phone / WhatsApp *</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="diwali-phone-input" className="w-full border border-burgundy-900/15 px-4 py-3 text-sm focus:outline-none focus:border-gold" placeholder="+91 ..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] tracking-wideluxe uppercase text-burgundy-900/60 mb-1.5 flex items-center gap-1.5"><Calendar size={12} /> Preferred Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} min="2026-10-06" max="2026-11-01" data-testid="diwali-date-input" className="w-full border border-burgundy-900/15 px-4 py-3 text-sm focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="block text-[11px] tracking-wideluxe uppercase text-burgundy-900/60 mb-1.5 flex items-center gap-1.5"><Clock size={12} /> Preferred Time</label>
              <select value={time} onChange={(e) => setTime(e.target.value)} data-testid="diwali-time-select" className="w-full border border-burgundy-900/15 px-4 py-3 text-sm focus:outline-none focus:border-gold bg-white">
                <option value="">Flexible</option>
                {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] tracking-wideluxe uppercase text-burgundy-900/60 mb-1.5">What are you dreaming of? (optional)</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="diwali-notes-input" className="w-full border border-burgundy-900/15 px-4 py-3 text-sm focus:outline-none focus:border-gold resize-none" placeholder="A festive saree, an Indo-western gown, colours you love…" />
          </div>
          <button type="submit" data-testid="diwali-submit-btn" className="w-full bg-burgundy-900 text-cream py-4 text-xs tracking-wideluxe uppercase hover:bg-burgundy-800 transition-colors flex items-center justify-center gap-2">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24z"/></svg>
            Reserve On WhatsApp
          </button>
          <p className="text-[11px] text-burgundy-900/45 text-center">No payment now — we confirm every slot personally before taking it on.</p>
        </form>
      </section>
    </div>
  );
}
